import type {
  InventoryItem,
  InventoryItemType,
  ItemAvailability,
  ItemCondition,
  ItemSource,
} from '@/features/admin/org-types';
import { db } from '@/firebase-config';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';

import type { OrganizationInventoryClient } from '../contracts';
import {
  isRecord,
  timestampDate,
} from './firestore-read-normalizers';

type ReadDocumentSnapshot = {
  id: string;
  data: () => unknown;
  exists?: () => boolean;
};

type QuerySnapshotLike = {
  docs: ReadDocumentSnapshot[];
};

const ORG_COLLECTION = 'organizations';
const INVENTORY_SUBCOLLECTION = 'inventory';

const INVENTORY_TYPES = new Set<InventoryItemType>([
  'prop',
  'costume',
  'sound',
  'lighting',
  'set',
  'paint',
]);

const ITEM_CONDITIONS = new Set<ItemCondition>([
  'excellent',
  'good',
  'fair',
  'needs_repair',
  'retired',
]);

const ITEM_AVAILABILITIES = new Set<ItemAvailability>([
  'available',
  'in_use',
  'reserved',
  'retired',
]);

const ITEM_SOURCES = new Set<ItemSource>([
  'stock',
  'purchased_new',
  'built',
  'donated',
]);

function dateOrNow(value: unknown): Date {
  return timestampDate(value) ?? new Date();
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readStoredString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function optionalStoredString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function preserveArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function inventoryType(value: unknown): InventoryItemType {
  return typeof value === 'string' && INVENTORY_TYPES.has(value as InventoryItemType)
    ? value as InventoryItemType
    : 'prop';
}

function itemCondition(value: unknown): ItemCondition {
  return typeof value === 'string' && ITEM_CONDITIONS.has(value as ItemCondition)
    ? value as ItemCondition
    : 'good';
}

function itemAvailability(value: unknown): ItemAvailability {
  return typeof value === 'string' && ITEM_AVAILABILITIES.has(value as ItemAvailability)
    ? value as ItemAvailability
    : 'available';
}

function itemSource(value: unknown): ItemSource {
  return typeof value === 'string' && ITEM_SOURCES.has(value as ItemSource)
    ? value as ItemSource
    : 'stock';
}

function toInventoryItem(docSnap: ReadDocumentSnapshot): InventoryItem {
  const data = isRecord(docSnap.data()) ? docSnap.data() as Record<string, unknown> : {};

  return {
    id: docSnap.id,
    type: inventoryType(data.type),
    name: readStoredString(data.name),
    description: optionalStoredString(data.description),
    storageLocation: optionalStoredString(data.storageLocation),
    condition: itemCondition(data.condition),
    availability: itemAvailability(data.availability),
    currentProjectId: optionalStoredString(data.currentProjectId),
    currentProjectTitle: optionalStoredString(data.currentProjectTitle),
    photos: preserveArray<InventoryItem['photos'][number]>(data.photos),
    originalProjectId: optionalStoredString(data.originalProjectId),
    originalProjectTitle: optionalStoredString(data.originalProjectTitle),
    acquiredDate: optionalStoredString(data.acquiredDate),
    source: itemSource(data.source),
    usageHistory: preserveArray<InventoryItem['usageHistory'][number]>(data.usageHistory),
    estimatedValue: optionalNumber(data.estimatedValue),
    createdAt: dateOrNow(data.createdAt),
    updatedAt: dateOrNow(data.updatedAt),
  };
}

export class FirebaseOrganizationInventoryClient implements OrganizationInventoryClient {
  async listInventory(orgId: string): Promise<InventoryItem[]> {
    const snapshot = await getDocs(
      collection(db, ORG_COLLECTION, orgId, INVENTORY_SUBCOLLECTION),
    ) as QuerySnapshotLike;

    return snapshot.docs.map(toInventoryItem);
  }

  async getInventoryItem(orgId: string, itemId: string): Promise<InventoryItem | null> {
    const snapshot = await getDoc(
      doc(db, ORG_COLLECTION, orgId, INVENTORY_SUBCOLLECTION, itemId),
    ) as ReadDocumentSnapshot;

    if (snapshot.exists && !snapshot.exists()) return null;
    return toInventoryItem(snapshot);
  }
}
